import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Collapse, Grid, Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ViewAndFilterButton from "../components/ViewAndFilterButton";
import ViewOption from "../components/ViewOption";
import FilterWrapper from "../components/general/FiltersWrapper";
import Loader from "../components/general/Loader";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NoData from "../components/general/NoData";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import ReusableTextField from "../components/general/ReusableTextField";
import ConfirmModal from "../components/modal/ConfirmModal";
import HeavyTable from "../components/table/HeavyTable";
import { showWarningSwal } from "../functions/alert";
import {
  buildQueryString,
  changeViewOptions,
  controlFilter,
  controlView,
  makeSureIsArray,
} from "../functions/general";
import {
  exportTeamBasedMembers,
  getTeamBasedMembers,
  getTeamData,
} from "../functions/getData";
import { updateTeamBasedMember } from "../functions/putData";
import { showNotification } from "../functions/snackbar";

const memberHeader = (isSimoc) =>
  [
    { id: "index_no", label: "Student Index" },
    { id: "name", label: "Student Name" },
    { id: "team_number", label: "Team Number" },
    { id: "division", label: "Division" },
    { id: "is_absent_title", label: "Is Absent" },
    isSimoc ? { id: "competition_event", label: "Event" } : null,
    { id: "score", label: "Score" },
    isSimoc ? { id: "score_percentage", label: "Score Percentage" } : null,
    { id: "system_award", label: "System Award" },
    { id: "final_award", label: "Final Award" },
  ].filter(Boolean);

export default function TeamBasedMembers() {
  document.title = "Competition Members";

  const location = useLocation();

  // Parse URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const isSimoc = searchParams.get("is_simoc") === "true";
  const isMounted = useRef(false);
  const [header, setHeader] = useState(() => memberHeader(isSimoc));
  const [memberTable, setMemberTable] = useState();
  const [viewOptions, setViewOptions] = useState(() => {
    const baseOptions = [
      { label: "Index", state: true, key: "index_no" },
      { label: "Name", state: true, key: "name" },
      { label: "Team Number", state: true, key: "team_number" },
      { label: "Is Absent", state: true, key: "is_absent_title" },
      { label: "Score", state: true, key: "score" },
      { label: "Score Percentage", state: true, key: "score_percentage" },
      { label: "System Award", state: true, key: "system_award" },
      { label: "Final Award", state: true, key: "final_award" },
    ];
    // Filter out Event and Score Percentage if isSimoc is true
    if (isSimoc) {
      return baseOptions.filter(
        (option) =>
          option.key !== "competition_event" &&
          option.key !== "score_percentage"
      );
    }
    return baseOptions;
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [id, setId] = useState("");
  const [selected, setSelected] = useState([]);
  const [selecting, setSelecting] = useState([]);
  const [onChangeStatus, setOnChangeStatus] = useState(false);

  // Consolidated query parameters state
  const [queryParams, setQueryParams] = useState({
    limits: 10,
    page: 1,
    search: "",
    award: "",
    division: "",
    aes_uploaded: "",
    in_team: "",
    orderBy: "",
    event: "",
  });

  const handleView = () => controlView(viewing, setViewing, setFiltering);
  const handleFilter = () => controlFilter(filtering, setFiltering, setViewing);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const isFirstRender = useRef(true);

  const updateQueryParams = (updates) => {
    setQueryParams((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const resetQueryParams = () => {
    setQueryParams({
      limits: 10,
      page: 1,
      search: "",
      award: "",
      division: "",
      aes_uploaded: "",
      orderBy: "",
      event: "",
    });
  };

  const handleChangeStatus = async () => {
    try {
      setLoading(true);
      const payload = {
        participant_index: id.index_no,
        team_number: id.team_number,
        division: id.division,
      };
      const response = await updateTeamBasedMember(competitionId, payload);
      showNotification(
        "success",
        "Status changed successfully",
        enqueueSnackbar,
        closeSnackbar
      );
      console.log(response);
    } catch (error) {
      showWarningSwal("Error in handleChangeStatus:", error);
    } finally {
      fetchData();
      setLoading(false);
      setOnChangeStatus(false);
    }
  };

  const adjustEndpoint = (endpoint, value) => {
    const currentParams = { ...queryParams };

    // Remove the current filter value if it's being changed
    if (value !== currentParams.award && currentParams.award) {
      endpoint += `&award=${currentParams.award}`;
    }
    if (value !== currentParams.event && currentParams.event) {
      endpoint += `&event=${currentParams.event}`;
    }
    if (value !== currentParams.division && currentParams.division) {
      endpoint += `&division=${currentParams.division}`;
    }
    if (value !== currentParams.aes_uploaded && currentParams.aes_uploaded) {
      endpoint += `&aes_uploaded=${currentParams.aes_uploaded}`;
    }
    if (value !== currentParams.in_team && currentParams.in_team) {
      endpoint += `&in_team=${currentParams.in_team}`;
    }

    return endpoint;
  };
  const competitionId = searchParams.get("id");

  const baseEndpoint = isSimoc
    ? `competition/team-based/simoc/${competitionId}/participants`
    : `competition/team-based/${competitionId}/participants`;
  const filters = [
    {
      label: "Award",
      endpoint: `${baseEndpoint}?get_filter=award`,
      value: queryParams.award,
      onChange: (value) =>
        updateQueryParams({
          award: value,
        }),
      key: "award",
      setValue: (value) =>
        updateQueryParams({
          award: value,
        }),
    },
    ...(isSimoc
      ? [
          {
            label: "Event",
            endpoint: `${baseEndpoint}?get_filter=event`,
            value: queryParams.event,
            onChange: (value) => updateQueryParams({ event: value }),
            key: "event",
            setValue: (value) => updateQueryParams({ event: value }),
          },
        ]
      : []),
    {
      label: "Division",
      endpoint: `${baseEndpoint}?get_filter=division`,
      value: queryParams.division,
      onChange: (value) =>
        updateQueryParams({
          division: value,
          // Preserve the current event value
          event: queryParams.event,
        }),
      key: "division",
      setValue: (value) =>
        updateQueryParams({
          division: value,
          // Preserve the current event value
          event: queryParams.event,
        }),
    },
    {
      label: "AES Uploaded",
      endpoint: `${baseEndpoint}?get_filter=aes_uploaded`,
      value: queryParams.aes_uploaded,
      onChange: (value) =>
        updateQueryParams({
          aes_uploaded: value,
          // Preserve the current event value
          event: queryParams.event,
        }),
      key: "aes_uploaded",
      setValue: (value) =>
        updateQueryParams({
          aes_uploaded: value,
          // Preserve the current event value
          event: queryParams.event,
        }),
    },
    {
      label: "In Team",
      endpoint: `${baseEndpoint}?get_filter=in_team`,
      value: queryParams.in_team,
      onChange: (value) => updateQueryParams({ in_team: value }),
      key: "in_team",
      setValue: (value) => updateQueryParams({ in_team: value }),
    },
  ];

  const handleSortingFunction = (sortingKey, direction) => {
    updateQueryParams({
      orderBy: sortingKey,
      event: queryParams.event,
    });
  };

  const onChangeViewOptions = (value) => {
    changeViewOptions(memberHeader(isSimoc), setViewOptions, setHeader, value);
    // Note: View options don't affect the API call, so no need to preserve event here
  };

  const onChangeSearch = (value) => {
    setSearch(value);
    updateQueryParams({
      search: value,
      page: 1,
      orderBy: "index",
      event: queryParams.event,
    });
  };

  const onChangePage = (pageNo) => {
    updateQueryParams({
      page: pageNo,
      // Preserve the current event value
      event: queryParams.event,
    });
  };

  const formatData = (data) => {
    console.log(data);
    return data.map((item) => ({
      ...item,
      is_absent_title:
        item.is_absent === 1 ? "Yes" : item.is_absent === 0 ? "No" : "-",
    }));
  };

  const onChangeRowsPerPage = (numberOfRows) => {
    setRowsPerPage(numberOfRows);
    updateQueryParams({
      limits: numberOfRows,
      // Preserve the current event value
      event: queryParams.event,
    });
  };

  const controller = useRef(null);

  // Effect for handling URL changes and initial setup
  useEffect(() => {
    if (isFirstRender.current) isFirstRender.current = false;
    isMounted.current = true;

    // Create a new abort controller for this effect
    controller.current = new AbortController();

    // Check if competition ID is provided in URL
    const competitionId = searchParams.get("id");
    if (competitionId) {
      // Initial data fetch
      setSelected([]);
      setSelecting([]);
      setLoading(true);

      const queryString = buildQueryString(queryParams);
      isSimoc
        ? getTeamBasedMembers(
            queryString,
            competitionId,
            controller.current.signal
          ).then((res) => {
            const formattedData = formatData(res?.data);
            res.data = formattedData;
            console.log(res);
            setMemberTable(res);
            setLoading(false);
          })
        : getTeamData(
            queryString,
            competitionId,
            controller.current.signal
          ).then((res) => {
            const formattedData = formatData(res?.data);
            res.data = formattedData;
            console.log(res);
            setMemberTable(res);
            setLoading(false);
          });
    } else {
      showWarningSwal("Competition ID is required");
    }

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
      isMounted.current = false;
    };
  }, [location.search, queryParams]);

  // Effect for data fetching
  useEffect(() => {
    const competitionId = searchParams.get("id");
    if (controller.current && !isFirstRender.current && competitionId) {
      setSelected([]);
      setSelecting([]);
      setLoading(true);

      const queryString = buildQueryString(queryParams);
      isSimoc
        ? getTeamBasedMembers(
            queryString,
            competitionId,
            controller.current.signal
          ).then((res) => {
            const formattedData = formatData(res?.data);
            res.data = formattedData;
            setMemberTable(res);
            setLoading(false);
          })
        : getTeamData(
            queryString,
            competitionId,
            controller.current.signal
          ).then((res) => {
            const formattedData = formatData(res?.data);
            res.data = formattedData;
            setMemberTable(res);
            setLoading(false);
          });
    }
  }, [queryParams]);

  const fetchData = async () => {
    const queryString = buildQueryString(queryParams);
    isSimoc
      ? getTeamBasedMembers(
          queryString,
          competitionId,
          controller.current.signal
        ).then((res) => {
          const formattedData = formatData(res?.data);
          res.data = formattedData;
          setMemberTable(res);
          setLoading(false);
        })
      : getTeamData(queryString, competitionId, controller.current.signal).then(
          (res) => {
            const formattedData = formatData(res?.data);
            res.data = formattedData;
            setMemberTable(res);
            setLoading(false);
          }
        );
  };

  const handleExport = async (id) => {
    try {
      setLoading(true);
      const queryString = buildQueryString(queryParams);
      await exportTeamBasedMembers(id, queryString);
    } catch (error) {
      console.error("Error in handleExport:", error);
      showWarningSwal(
        "error",
        "Failed to export team based members",
        enqueueSnackbar,
        closeSnackbar
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={loading} />
      <Collapse in={viewing}>
        <ViewOption
          onChangeRowsPerPage={onChangeRowsPerPage}
          viewOptions={viewOptions}
          rowsPerPage={rowsPerPage}
          onChangeViewOptions={onChangeViewOptions}
        />
      </Collapse>
      <Collapse in={filtering}>
        <FilterWrapper filters={filters} adjustEndpoint={adjustEndpoint} />
      </Collapse>
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Competition Members"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
          />
        </div>
        <div className="viewAndFilterBtnDiv">
          <ViewAndFilterButton
            text="View Options"
            state={viewing}
            fontSize={14}
            height={42}
            onClick={handleView}
            marginRight={10}
          />
          <ViewAndFilterButton
            text="Filter"
            state={filtering}
            fontSize={14}
            height={42}
            onClick={handleFilter}
          />
        </div>
      </Grid>
      <Grid className="tableContainer" container>
        <NunitoText
          value="Competition Members"
          fontSize={40}
          fontWeight={700}
          italic
          color="#144A94"
        />
        <Grid
          className="searchAndBtnContainer"
          container
          alignItems="center"
          justifyContent="space-between"
        >
          <ReusableTextField
            type="search"
            width={500}
            height={60}
            bgColor="#F2F2F2"
            placeholder="Search using keyword"
            state={search}
            setState={onChangeSearch}
            onBlur
          />
          <ReusableButton
            text="Export List"
            fontSize={15}
            bgColor="#5E75C3"
            height={46}
            marginRight={20}
            iconType="export"
            onClick={() => handleExport(competitionId)}
          />
        </Grid>
        <NunitoText
          value="Search in student Name , Student Index and team number"
          fontSize={20}
          fontWeight={400}
          italic
          color="#144A94"
        />
        {loadingTable && <Loader height={600} />}
        {!loadingTable &&
          memberTable &&
          makeSureIsArray(memberTable.data).length && (
            <HeavyTable
              headers={header}
              list={memberTable}
              title="Team Based Members"
              rowsPerPage={rowsPerPage}
              onChangePage={onChangePage}
              fixed={["index", "name"]}
              selecting={selecting}
              setSelecting={setSelecting}
              selected={selected}
              setSelected={setSelected}
              hideCheckBoxes={true}
              setId={setId}
              setOnChangeStatus={setOnChangeStatus}
            />
          )}
        <Modal open={onChangeStatus} onClose={() => setOnChangeStatus(false)}>
          <ConfirmModal
            action="change status "
            confirmFunction={() => handleChangeStatus()}
            cancelConfirm={() => setOnChangeStatus(false)}
            message={`Are you sure you want to change the status of the selected student to ${
              id?.is_absent === 0 ? "Absent" : "Active"
            }?`}
          />
        </Modal>
        {!loadingTable &&
          memberTable &&
          !makeSureIsArray(memberTable.data).length && <NoData height={600} />}
      </Grid>
    </Box>
  );
}
